import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import { cn } from '@/lib/utils'
import { useFluidAnimation } from '@/hooks/use-fluid-animation'
import { useSidebarLiquidGlass } from '@/hooks/use-sidebar-liquid-glass'

/**
 * FluidHoverIndicatorV2 Props
 */
export interface FluidHoverIndicatorV2Props {
  /** Target element to track */
  targetElement: HTMLElement | null
  /** Whether the indicator should be visible */
  isVisible: boolean
  /** Optional className for customization */
  className?: string
  /** Container ref for relative positioning (default: targetElement.parentElement) */
  containerRef?: RefObject<HTMLElement>
  /** Enable chromatic aberration (Phase 2) */
  enableChromaticAberration?: boolean
}

/**
 * Fluid Hover Indicator V2 - Con Liquid Glass Effect
 *
 * @description
 * Versión mejorada del FluidHoverIndicator que combina:
 * - **Spring physics** del original (anticipation, stretch, overshoot)
 * - **Liquid glass effect** con SVG displacement mapping
 * - **Refracción real** según Snell's Law
 * - **Chromatic aberration** RGB opcional (Phase 2)
 * - **Backdrop extension** para capturar elementos cercanos
 *
 * Features del research aplicadas:
 * - ✅ SVG Displacement (Kube.io) → Refracción física
 * - ✅ Chromatic Aberration (Atlas Pup Labs) → RGB channels
 * - ✅ Performance Optimizations → GPU acceleration
 * - ✅ Backdrop Extension (Josh Comeau) → Captura elementos nearby
 *
 * @example
 * ```tsx
 * <SidebarNav>
 *   <FluidHoverIndicatorV2
 *     targetElement={hoveredItem}
 *     isVisible={isHovering}
 *     enableChromaticAberration={true}
 *   />
 * </SidebarNav>
 * ```
 */
export function FluidHoverIndicatorV2({
  targetElement,
  isVisible,
  className = '',
  containerRef,
  enableChromaticAberration = false
}: FluidHoverIndicatorV2Props) {
  const indicatorRef = useRef<HTMLDivElement>(null)
  const { animate, reset } = useFluidAnimation()
  const isFirstRender = useRef(true)

  // Generar liquid glass effect con preset optimizado
  const { dataUri, filterAttributes, chromaticAberrationSVG } = useSidebarLiquidGlass(
    'indicator',
    { enableChromaticAberration }
  )

  // Debug: Log liquid glass data
  useEffect(() => {
    console.log('🔬 Liquid Glass V2 Data:', {
      hasDataUri: !!dataUri,
      dataUriLength: dataUri?.length || 0,
      hasFilterAttributes: !!filterAttributes,
      hasChromaticSVG: !!chromaticAberrationSVG,
      enabledChromatic: enableChromaticAberration,
      filterIds: {
        red: filterAttributes?.red?.xChannelSelector,
        green: filterAttributes?.green?.xChannelSelector,
        blue: filterAttributes?.blue?.xChannelSelector
      }
    })
  }, [dataUri, filterAttributes, chromaticAberrationSVG, enableChromaticAberration])

  // Spring physics animation (mantener del original)
  useEffect(() => {
    if (!targetElement || !indicatorRef.current || !isVisible) {
      if (!isVisible && indicatorRef.current) {
        reset()
        indicatorRef.current.style.opacity = '0'
        isFirstRender.current = true
      }
      return
    }

    // Get target position
    const targetRect = targetElement.getBoundingClientRect()
    const parentRect = containerRef?.current?.getBoundingClientRect()
      || targetElement.parentElement?.getBoundingClientRect()

    if (!parentRect) return

    // Calculate position relative to sidebar
    const padding = 8 // Space for the sidebar indicator bar
    const position = {
      x: targetRect.left - parentRect.left + padding,
      y: targetRect.top - parentRect.top + 4,
      width: targetRect.width - padding - 4,
      height: targetRect.height - 8,
    }

    // First render: instant positioning
    if (isFirstRender.current) {
      indicatorRef.current.style.transform = `translate(${position.x}px, ${position.y}px)`
      indicatorRef.current.style.width = `${position.width}px`
      indicatorRef.current.style.height = `${position.height}px`
      indicatorRef.current.style.opacity = '1'
      isFirstRender.current = false
      return
    }

    // Animate to new position with fluid physics
    animate(indicatorRef.current, position)
  }, [targetElement, isVisible, animate, reset, containerRef])

  return (
    <div
      ref={indicatorRef}
      className={cn(
        // Base styling
        'pointer-events-none absolute z-20',
        'rounded-xl',
        // GPU acceleration (Phase 1 performance optimization)
        'will-change-transform',
        // Visibility
        isVisible ? 'opacity-100' : 'opacity-0',
        // Transition for opacity only (position handled by animate())
        'transition-opacity duration-200',
        // Custom classes
        className
      )}
      style={{
        // Performance optimization (Phase 1)
        contain: 'layout style paint',
        // Initial state
        transform: 'translate(0, 0) scale(1) skew(0deg)',
        // Force hide when not visible
        opacity: isVisible ? undefined : 0,
      }}
    >
      {/* SVG Filter Definition con Liquid Glass */}
      <svg className="absolute inset-0 pointer-events-none w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="liquid-glass-indicator" colorInterpolationFilters="sRGB">
            {/* Displacement Map generado con refracción física */}
            <feImage
              x="0"
              y="0"
              width="100%"
              height="100%"
              result="map"
              href={dataUri}
            />

            {/* Phase 1: Legacy chromatic aberration con scale offsets */}
            {!enableChromaticAberration && (
              <>
                {/* Red channel */}
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="map"
                  xChannelSelector={filterAttributes.red.xChannelSelector}
                  yChannelSelector={filterAttributes.red.yChannelSelector}
                  scale={filterAttributes.red.scale}
                  result="dispRed"
                />
                <feColorMatrix
                  in="dispRed"
                  type="matrix"
                  values="1 0 0 0 0
                          0 0 0 0 0
                          0 0 0 0 0
                          0 0 0 1 0"
                  result="red"
                />

                {/* Green channel */}
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="map"
                  xChannelSelector={filterAttributes.green.xChannelSelector}
                  yChannelSelector={filterAttributes.green.yChannelSelector}
                  scale={filterAttributes.green.scale}
                  result="dispGreen"
                />
                <feColorMatrix
                  in="dispGreen"
                  type="matrix"
                  values="0 0 0 0 0
                          0 1 0 0 0
                          0 0 0 0 0
                          0 0 0 1 0"
                  result="green"
                />

                {/* Blue channel */}
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="map"
                  xChannelSelector={filterAttributes.blue.xChannelSelector}
                  yChannelSelector={filterAttributes.blue.yChannelSelector}
                  scale={filterAttributes.blue.scale}
                  result="dispBlue"
                />
                <feColorMatrix
                  in="dispBlue"
                  type="matrix"
                  values="0 0 0 0 0
                          0 0 0 0 0
                          0 0 1 0 0
                          0 0 0 1 0"
                  result="blue"
                />

                {/* Blend RGB channels */}
                <feBlend in="red" in2="green" mode="screen" result="rg" />
                <feBlend in="rg" in2="blue" mode="screen" result="output" />

                {/* Final blur */}
                <feGaussianBlur
                  in="output"
                  stdDeviation={filterAttributes.gaussianBlur.stdDeviation}
                />
              </>
            )}

            {/* Phase 2: True chromatic aberration con feColorMatrix + feOffset */}
            {enableChromaticAberration && chromaticAberrationSVG && (
              <>
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="map"
                  xChannelSelector={filterAttributes.red.xChannelSelector}
                  yChannelSelector={filterAttributes.red.yChannelSelector}
                  scale={filterAttributes.red.scale}
                  result="displaced"
                />
                <g dangerouslySetInnerHTML={{ __html: chromaticAberrationSVG }} />
                <feGaussianBlur
                  in="chromaticAberration"
                  stdDeviation={filterAttributes.gaussianBlur.stdDeviation}
                />
              </>
            )}
          </filter>
        </defs>
      </svg>

      {/* Backdrop extension layer - 200% height para capturar más contenido */}
      <div
        className="absolute inset-0 rounded-xl pointer-events-none"
        style={{
          height: '200%',
          backdropFilter: 'blur(6px) url(#liquid-glass-indicator) brightness(1.1) saturate(1.5)',
          WebkitBackdropFilter: 'blur(6px) url(#liquid-glass-indicator) brightness(1.1) saturate(1.5)',
          maskImage: 'linear-gradient(to bottom, black 0% 50%, transparent 50% 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0% 50%, transparent 50% 100%)',
          zIndex: 0
        }}
      />

      {/* Main glass layer - usando frost variable */}
      <div
        className="absolute inset-0 rounded-xl border border-primary/60"
        style={{
          // CRÍTICO: backdrop-filter con blur() ANTES de url(#filter)
          backdropFilter: 'blur(6px) url(#liquid-glass-indicator) brightness(1.1) saturate(1.5)',
          WebkitBackdropFilter: 'blur(6px) url(#liquid-glass-indicator) brightness(1.1) saturate(1.5)',
          // Background usando frost (5% opacity)
          background: 'hsl(0 0% 50% / 0.05)',
          // Box shadow inset para profundidad
          boxShadow: `
            0 0 2px 1px rgba(99, 102, 241, 0.15) inset,
            0 0 10px 4px rgba(99, 102, 241, 0.1) inset,
            0px 4px 16px rgba(17, 17, 26, 0.05),
            0px 8px 24px rgba(17, 17, 26, 0.05)
          `,
          pointerEvents: 'none'
        }}
      />
    </div>
  )
}
